import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useEnvironmentsStore } from '@/stores/environments';
import { useUserStore } from '@/stores/user';

export const RETRY_DELAYS_MS = [1000, 2000, 4000];
export const ATTEMPT_TIMEOUT_MS = 10_000;
export const RECOVERY_INTERVAL_MS = 30_000;
export const RECOVERY_JITTER_MS = 5_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = <T>(promise: Promise<T>) =>
  Promise.race([
    promise,
    sleep(ATTEMPT_TIMEOUT_MS).then(() => Promise.reject(new Error('Attempt timed out'))),
  ]);

const isRetryable = (error: any) => !error?.response || error.response.status >= 500;

export const createStartupData = () => {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const environmentsStore = useEnvironmentsStore();
  const visibility = useDocumentVisibility();

  const pageLoads = ref(0);
  const isLoading = computed(() => pageLoads.value > 0);
  const isUnavailable = ref(false);
  const isEnvironmentsLoaded = ref(false);
  const isEnvironmentsFailed = ref(false);
  const pendingRoute = ref<string | null>(null);

  let isRecovering = false;
  let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
  let userRequest: Promise<unknown> | null = null;
  let userLoads = 0;

  const fetchEnvironments = () =>
    withTimeout(environmentsStore.getEnvironments().then(() => {
      isEnvironmentsFailed.value = false;
      isEnvironmentsLoaded.value = true;
      hideIfRecovered();
    }));

  const fetchUser = () =>
    (userRequest ??= withTimeout(userStore.getUser()).finally(() => {
      userRequest = null;
    }));

  const loadWithRetry = async (load: () => Promise<unknown>, holdsPage: boolean) => {
    if (holdsPage) {
      pageLoads.value++;
    }

    try {
      for (let attempt = 0; ; attempt++) {
        try {
          return await load();
        } catch (error) {
          const delay = RETRY_DELAYS_MS[attempt];
          if (delay === undefined || !isRetryable(error) || (holdsPage && !authStore.isAuthorized.value)) {
            throw error;
          }
          await sleep(delay);
        }
      }
    } finally {
      if (holdsPage) {
        pageLoads.value--;
      }
    }
  };

  const scheduleRecovery = (delay: number) => {
    clearTimeout(recoveryTimer);
    recoveryTimer = setTimeout(() => {
      if (visibility.value === 'hidden') {
        scheduleRecovery(RECOVERY_INTERVAL_MS);
      } else {
        recover();
      }
    }, delay + Math.random() * RECOVERY_JITTER_MS);
  };

  const showUnavailable = () => {
    isUnavailable.value = true;
    scheduleRecovery(RECOVERY_INTERVAL_MS);
  };

  const hideUnavailable = () => {
    isUnavailable.value = false;
    pendingRoute.value = null;
    clearTimeout(recoveryTimer);
  };

  const hideIfRecovered = () => {
    if (!pendingRoute.value && !isEnvironmentsFailed.value) {
      hideUnavailable();
    }
  };

  const recover = async () => {
    if (isRecovering || !isUnavailable.value) {
      return;
    }
    isRecovering = true;
    clearTimeout(recoveryTimer);

    try {
      if (isEnvironmentsFailed.value) {
        await fetchEnvironments();
      }
      const target = pendingRoute.value;
      if (target && !userStore.user) {
        await fetchUser();
      }
      const isTargetCurrent = target === pendingRoute.value;
      hideUnavailable();
      if (target && isTargetCurrent) {
        await router.replace(target);
      }
    } catch {
      if (authStore.isAuthorized.value) {
        scheduleRecovery(RECOVERY_INTERVAL_MS);
      } else {
        hideUnavailable();
      }
    } finally {
      isRecovering = false;
    }
  };

  const loadEnvironments = async () => {
    try {
      await loadWithRetry(fetchEnvironments, authStore.isAuthorized.value);
      return true;
    } catch {
      isEnvironmentsFailed.value = true;
      if (authStore.isAuthorized.value) {
        showUnavailable();
      }
      return false;
    }
  };

  const loadUser = async (target: string) => {
    if (userStore.user) {
      return true;
    }

    if (isUnavailable.value) {
      pendingRoute.value = target;
    }
    userLoads++;
    try {
      await loadWithRetry(fetchUser, true);
      pendingRoute.value = null;
      hideIfRecovered();
      return true;
    } catch {
      if (authStore.isAuthorized.value) {
        pendingRoute.value = target;
        showUnavailable();
      }
      return false;
    } finally {
      userLoads--;
    }
  };

  useEventListener(window, 'online', () => {
    if (isUnavailable.value) {
      scheduleRecovery(0);
    }
  });

  watch(visibility, (current) => {
    if (current === 'visible' && isUnavailable.value) {
      scheduleRecovery(0);
    }
  });

  watch(() => userStore.user, (user) => {
    if (user && isUnavailable.value && userLoads === 0) {
      recover();
    }
  });

  watch(authStore.isAuthorized, (authorized) => {
    if (authorized && isEnvironmentsFailed.value) {
      loadEnvironments();
    }
  });

  return { isLoading, isUnavailable, isEnvironmentsLoaded, loadEnvironments, loadUser, recover };
};

export const useStartupData = createGlobalState(createStartupData);
